import type { CSSProperties, ReactNode } from 'react'

import { cn } from '@/lib/cn'

export type DataTableSortDirection = 'asc' | 'desc'

export interface DataTableColumnSort {
  direction: DataTableSortDirection | null
  disabled?: boolean
  label: string
  onToggle: () => void
}

export interface DataTableColumn<T> {
  align?: 'center' | 'left' | 'right'
  header: ReactNode
  headerAlign?: 'center' | 'left' | 'right'
  hidden?: boolean
  id: string
  pin?: 'left' | 'right'
  render: (row: T) => ReactNode
  sort?: DataTableColumnSort
  visibilityLabel?: string
  width: number
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  empty?: ReactNode
  getRowKey: (row: T) => string
  isLoading?: boolean
  minWidth?: number
  rows: T[]
}

const alignClassName = {
  center: 'text-center',
  left: 'text-left',
  right: 'text-right',
} as const

const headerContentAlignClassName = {
  center: 'justify-center',
  left: 'justify-start',
  right: 'justify-end',
} as const

function pinClassName(pin: DataTableColumn<unknown>['pin']) {
  if (pin === 'left') {
    return 'sticky z-10 shadow-[1px_0_0_var(--color-divider)]'
  }

  if (pin === 'right') {
    return 'sticky z-10 shadow-[-1px_0_0_var(--color-divider)]'
  }

  return undefined
}

function getPinnedOffsets<T>(columns: DataTableColumn<T>[]) {
  const offsets = new Map<string, CSSProperties>()
  let leftOffset = 0

  for (const column of columns) {
    if (column.pin === 'left') {
      offsets.set(column.id, { left: leftOffset })
      leftOffset += column.width
    }
  }

  let rightOffset = 0
  for (const column of [...columns].reverse()) {
    if (column.pin === 'right') {
      offsets.set(column.id, { right: rightOffset })
      rightOffset += column.width
    }
  }

  return offsets
}

function sortAriaValue(direction: DataTableSortDirection | null | undefined) {
  if (direction === 'asc') {
    return 'ascending'
  }

  if (direction === 'desc') {
    return 'descending'
  }

  return 'none'
}

function SortIndicator({ direction }: { direction: DataTableSortDirection | null | undefined }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex size-4 shrink-0 items-center justify-center text-[var(--color-text-muted)]',
        direction ? 'text-[var(--color-primary)]' : null,
      )}
    >
      <svg className="size-3" fill="none" viewBox="0 0 12 12">
        {direction === 'asc' ? (
          <path
            d="M6 2.5v7M3.5 5 6 2.5 8.5 5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        ) : (
          <path
            d="M6 9.5v-7M3.5 7 6 9.5 8.5 7"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        )}
      </svg>
    </span>
  )
}

function DataTableHeaderCell<T>({ column }: { column: DataTableColumn<T> }) {
  const headerAlign = column.headerAlign ?? column.align ?? 'left'

  if (!column.sort) {
    return column.header
  }

  return (
    <button
      aria-label={column.sort.label}
      className={cn(
        'inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-sm text-inherit transition-colors',
        'hover:text-[var(--color-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
        'disabled:cursor-not-allowed disabled:opacity-60',
        headerContentAlignClassName[headerAlign],
      )}
      disabled={column.sort.disabled}
      onClick={column.sort.onToggle}
      type="button"
    >
      <span className="truncate">{column.header}</span>
      <SortIndicator direction={column.sort.direction} />
    </button>
  )
}

export function DataTable<T>({
  columns,
  empty,
  getRowKey,
  isLoading = false,
  minWidth,
  rows,
}: DataTableProps<T>) {
  const visibleColumns = columns.filter((column) => !column.hidden)
  const tableMinWidth =
    minWidth ?? visibleColumns.reduce((total, column) => total + column.width, 0)
  const pinnedOffsets = getPinnedOffsets(visibleColumns)

  return (
    <div className="min-h-0 overflow-hidden rounded-lg border border-[var(--color-panel-border)] bg-white shadow-[var(--shadow-panel-subtle)]">
      <div className="data-table-scroll min-w-0 overflow-x-auto">
        <table
          className="w-full border-collapse text-sm leading-5 text-[var(--color-text)]"
          style={{ minWidth: tableMinWidth }}
        >
          <colgroup>
            {visibleColumns.map((column) => (
              <col key={column.id} style={{ width: column.width }} />
            ))}
          </colgroup>
          <thead>
            <tr className="h-[46px] border-b border-[var(--color-divider)] bg-[#fbfbfb]">
              {visibleColumns.map((column) => (
                <th
                  aria-sort={column.sort ? sortAriaValue(column.sort.direction) : undefined}
                  className={cn(
                    'bg-[#fbfbfb] px-5 text-sm font-semibold whitespace-nowrap text-[var(--color-text)]',
                    alignClassName[column.headerAlign ?? column.align ?? 'left'],
                    pinClassName(column.pin),
                    column.pin ? 'z-20' : null,
                  )}
                  key={column.id}
                  scope="col"
                  style={pinnedOffsets.get(column.id)}
                >
                  <DataTableHeaderCell column={column} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 8 }, (_, index) => (
                  <tr className="h-[46px] border-b border-[var(--color-divider)]" key={index}>
                    {visibleColumns.map((column) => (
                      <td
                        className={cn(
                          'px-5',
                          alignClassName[column.align ?? 'left'],
                          pinClassName(column.pin),
                          column.pin ? 'bg-white' : null,
                        )}
                        key={column.id}
                        style={pinnedOffsets.get(column.id)}
                      >
                        <div className="h-3 w-3/4 animate-pulse rounded-full bg-[var(--color-skeleton)]" />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row) => (
                  <tr
                    className="group h-[46px] border-b border-[var(--color-divider)] last:border-b-0 hover:bg-[#fafafa]"
                    key={getRowKey(row)}
                  >
                    {visibleColumns.map((column) => (
                      <td
                        className={cn(
                          'px-5 align-middle whitespace-nowrap',
                          alignClassName[column.align ?? 'left'],
                          pinClassName(column.pin),
                          column.pin ? 'bg-white group-hover:bg-[#fafafa]' : null,
                        )}
                        key={column.id}
                        style={pinnedOffsets.get(column.id)}
                      >
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      {!isLoading && rows.length === 0 ? (
        <div className="flex min-h-40 items-center justify-center border-t border-[var(--color-divider)] px-6 py-10 text-sm text-[var(--color-text-muted)]">
          {empty}
        </div>
      ) : null}
    </div>
  )
}
